import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Calendar, ArrowRight, Loader2, AlertCircle, FileText, Phone, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { getStoredPartner, partnerHome } from '../lib/partnerContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConseilConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerHomePath = partnerHome(getStoredPartner());
  const sessionId = searchParams.get('session_id');
  const [conseilId, setConseilId] = useState(searchParams.get('conseil_id'));
  const [trackingCode, setTrackingCode] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setError('Session invalide');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setLoading(false);
        setPaymentStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/api/conseil/checkout-status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setPaymentStatus('paid');
          setConseilId(data.conseil_id);
          setTrackingCode(data.tracking_code || '');
          setClientEmail(data.email || '');
          setLoading(false);
          return;
        } else if (data.status === 'expired') {
          setPaymentStatus('expired');
          setLoading(false);
          return;
        }

        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error('Error checking status:', err);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        } else {
          setError('Erreur de vérification');
          setLoading(false);
        }
      }
    };

    pollStatus();
  }, [sessionId]);

  const copyCode = () => {
    if (!trackingCode) return;
    navigator.clipboard.writeText(trackingCode);
    toast.success('Code copié !');
  };

  const goToRdv = () => {
    if (!conseilId || !clientEmail) return;
    navigate(`/conseil/rdv?id=${conseilId}&email=${encodeURIComponent(clientEmail)}&code=${trackingCode}`);
  };

  const goToDossier = () => {
    if (!conseilId || !clientEmail) return;
    navigate(`/conseil/suivi?id=${conseilId}&email=${encodeURIComponent(clientEmail)}&code=${trackingCode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-white/20 max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-gold mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Vérification du paiement...</h2>
            <p className="text-white/70">Veuillez patienter quelques secondes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-white/20 max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              {paymentStatus === 'timeout' ? 'Vérification en cours...' : 'Session expirée'}
            </h2>
            <p className="text-white/70 mb-6">
              {paymentStatus === 'timeout'
                ? 'Le paiement est peut-être en cours de traitement. Vérifiez votre email.'
                : 'Veuillez réessayer le paiement.'}
            </p>
            <Button onClick={() => navigate(partnerHomePath)} className="bg-gold hover:bg-gold/90 text-black" data-testid="back-home-btn">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4" data-testid="conseil-confirmation-page">
      <Card className="bg-white max-w-xl w-full shadow-2xl">
        <CardContent className="pt-8 pb-8">
          {/* Success Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-2">
              Paiement confirmé !
            </h1>
            <p className="text-muted-foreground">
              Facture envoyée par email avec votre code de suivi.
            </p>
          </div>

          {/* Amount */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 text-center">
            <p className="text-emerald-700 font-semibold">
              <span className="text-2xl">99€</span> TTC débités
            </p>
          </div>

          {/* Tracking code */}
          {trackingCode && (
            <div className="bg-gradient-to-r from-gold/10 to-amber-50 border-2 border-gold/40 rounded-xl p-5 mb-6 text-center" data-testid="conseil-tracking-code-box">
              <p className="text-navy/60 text-xs uppercase tracking-wide mb-1">Votre code de suivi</p>
              <div className="flex items-center justify-center gap-2">
                <p className="font-mono text-2xl font-bold text-navy tracking-wider" data-testid="conseil-tracking-code">{trackingCode}</p>
                <button onClick={copyCode} className="text-gold hover:text-gold/80 p-1" title="Copier" data-testid="copy-tracking-code-btn">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-navy/60 text-xs mt-2">Conservez ce code pour retrouver votre dossier</p>
            </div>
          )}

          {/* Next Step - Internal Calendar */}
          <div className="bg-gradient-to-r from-gold/10 to-amber-50 border-2 border-gold/30 rounded-xl p-6 mb-4">
            <h3 className="font-heading font-bold text-navy mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" />
              Prochaine étape : Réservez votre créneau
            </h3>
            <p className="text-navy/70 text-sm mb-4">
              Choisissez le moment qui vous convient pour notre échange de 45min à 1h.
            </p>
            <Button
              data-testid="book-rdv-btn"
              onClick={goToRdv}
              disabled={!conseilId || !clientEmail}
              className="w-full bg-gold hover:bg-gold/90 text-black font-semibold py-6"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Choisir mon créneau
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* Accéder au dossier */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Ajoutez vos documents
            </h4>
            <p className="text-sm text-blue-800 mb-3">
              Uploadez vos devis, photos ou plans pour que je puisse préparer notre échange.
            </p>
            <Button
              data-testid="go-to-dossier-btn"
              onClick={goToDossier}
              disabled={!conseilId || !clientEmail}
              variant="outline"
              className="w-full border-blue-400 text-blue-900 hover:bg-blue-100"
            >
              Accéder à mon dossier
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Contact Info */}
          <div className="text-center text-sm text-muted-foreground mb-4">
            <p className="flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              Un problème ? Répondez directement à l'email de confirmation.
            </p>
          </div>

          {/* Back Button */}
          <div className="text-center">
            <Button variant="ghost" onClick={() => navigate(partnerHomePath)} className="text-navy" data-testid="back-home-btn">
              Retour à l'accueil
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
